import { useState, useEffect } from 'react';
import { User, Mail, Phone, MapPin, Edit, Save, X, CheckCircle, Lock, Eye, EyeOff, Calendar, Briefcase, Building, Users, CreditCard } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useMySouscriptions } from '../../hooks/useClientApi';
import { useReferenceData } from '../../hooks/useReferenceData';
import { clientApiService } from '../../services/clientApi';
import Breadcrumb from '../../components/common/Breadcrumb';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import { useTexts } from '../../hooks/useTexts';

export default function ExtendedProfilePage() {
  const { getText } = useTexts();
  const { user, refreshUser } = useAuth();
  const { statutsMaritaux, regimesMatrimoniaux, loading: refLoading } = useReferenceData();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  const [formData, setFormData] = useState({
    nom: user?.nom || '',
    prenoms: user?.prenoms || '',
    email: user?.email || '',
    telephone: user?.telephone || '',
    adresse: user?.adresse || '',
    lieu_naissance: user?.lieu_naissance || '',
    nationalite: user?.nationalite || '',
    numero_piece_identite: user?.numero_piece_identite || '',
    entreprise: user?.entreprise || '',
    profession: user?.profession || '',
    revenu_mensuel: user?.revenu_mensuel || '',
    domiciliation_bancaire: user?.domiciliation_bancaire || '',
    statut_marital: user?.statut_marital || '',
    regime_matrimonial: user?.regime_matrimonial || '',
    nombre_enfants: user?.nombre_enfants || '',
    date_naissance: user?.date_naissance || ''
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [avatar, setAvatar] = useState(user?.avatar || '');

  // Récupérer les données des souscriptions pour les statistiques
  const { data: souscriptionsData, loading: souscriptionsLoading } = useMySouscriptions();
  const subscriptions = souscriptionsData?.results || [];

  // Mettre à jour le formulaire quand l'utilisateur change
  useEffect(() => {
    if (user) {
      setFormData({
        nom: user.nom || '',
        prenoms: user.prenoms || '',
        email: user.email || '',
        telephone: user.telephone || '',
        adresse: user.adresse || '',
        lieu_naissance: user.lieu_naissance || '',
        nationalite: user.nationalite || '',
        numero_piece_identite: user.numero_piece_identite || '',
        entreprise: user.entreprise || '',
        profession: user.profession || '',
        revenu_mensuel: user.revenu_mensuel || '',
        domiciliation_bancaire: user.domiciliation_bancaire || '',
        statut_marital: user.statut_marital || '',
        regime_matrimonial: user.regime_matrimonial || '',
        nombre_enfants: user.nombre_enfants || '',
        date_naissance: user.date_naissance || ''
      });
      setAvatar(user.avatar || '');
    }
  }, [user]);

  // Calculer les statistiques
  const stats = {
    totalSubscriptions: subscriptions.length,
    activeSubscriptions: subscriptions.filter(s => s.statut_souscription === 'ACTIVE').length,
    totalPaid: subscriptions.reduce((sum, sub) => {
      const prixConvenu = Number(sub.prix_convenu_ht) || 0;
      const montantRestant = Number(sub.montant_restant) || 0;
      return sum + (prixConvenu - montantRestant);
    }, 0),
    totalRemaining: subscriptions.reduce((sum, sub) => sum + (Number(sub.montant_restant) || 0), 0)
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Exclure l'email des données à mettre à jour
      const { email, ...updateData } = formData;
      await clientApiService.updateMyProfile(user.id, updateData);
      setSuccess('Profil mis à jour avec succès !');
      setIsEditing(false);
      
      // Rafraîchir les données utilisateur dans le contexte
      await refreshUser();
      
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors de la mise à jour du profil');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Les nouveaux mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError('Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setSaving(true);
    setPasswordError(null);
    setPasswordSuccess(null);

    try {
      const passwordUpdateData = {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        confirm_password: passwordData.confirmPassword
      };
      await clientApiService.changeMyPassword(user.id, passwordUpdateData);
      
      setPasswordSuccess('Mot de passe modifié avec succès !');
      setIsChangingPassword(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
    } catch (err: any) {
      setPasswordError(err.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Utiliser les données utilisateur actuelles du contexte
    setFormData({
      nom: user?.nom || '',
      prenoms: user?.prenoms || '',
      email: user?.email || '',
      telephone: user?.telephone || '',
      adresse: user?.adresse || '',
      lieu_naissance: user?.lieu_naissance || '',
      nationalite: user?.nationalite || '',
      numero_piece_identite: user?.numero_piece_identite || '',
      entreprise: user?.entreprise || '',
      profession: user?.profession || '',
      revenu_mensuel: user?.revenu_mensuel || '',
      domiciliation_bancaire: user?.domiciliation_bancaire || '',
      statut_marital: user?.statut_marital || '',
      regime_matrimonial: user?.regime_matrimonial || '',
      nombre_enfants: user?.nombre_enfants || '',
      date_naissance: user?.date_naissance || ''
    });
    setIsEditing(false);
  };

  const handlePasswordCancel = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setIsChangingPassword(false);
    setPasswordError(null);
    setPasswordSuccess(null);
  };

  // Affichage de l'état de chargement si l'utilisateur n'est pas encore chargé
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: getText('profile.title'), current: true }
            ]}
          />
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{getText('profile.title')}</h1>
          <p className="text-gray-600 mt-2">
            {getText('profile.description')}
          </p>
        </div>

        {/* Messages d'erreur et de succès */}
        {error && (
          <div className="mb-6">
            <ErrorMessage error={error} onDismiss={() => setError(null)} />
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <p className="text-green-800">{success}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
              <div className="relative inline-block mb-6">
                <div className="w-32 h-32 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  {avatar ? (
                    <img src={avatar} alt="Avatar" className="w-32 h-32 rounded-full object-cover" />
                  ) : (
                    <User className="w-16 h-16 text-blue-600" />
                  )}
                </div>
              </div>
              
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {user?.prenoms} {user?.nom}
              </h2>
              <p className="text-gray-600 mb-4">{user?.email}</p>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-center text-gray-600">
                  <Phone className="w-4 h-4 mr-2" />
                  {user?.telephone}
                </div>
                <div className="flex items-center justify-center text-gray-600">
                  <MapPin className="w-4 h-4 mr-2" />
                  {user?.adresse}
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-xl font-bold text-blue-600">
                      {souscriptionsLoading ? '...' : stats.totalSubscriptions}
                    </div>
                    <div className="text-xs text-gray-600">Souscriptions</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-green-600">
                      {user?.statut === 'ACTIF' ? 'Actif' : 'Inactif'}
                    </div>
                    <div className="text-xs text-gray-600">Statut</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Informations Personnelles
                </h3>
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Modifier
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleCancel}
                      className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Annuler
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={saving}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sauvegarde...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Sauvegarder
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Informations de base */}
                <div className="border-b border-gray-200 pb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    Informations de base
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
                        Nom
                      </label>
                      <input
                        type="text"
                        id="nom"
                        name="nom"
                        value={formData.nom}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="prenoms" className="block text-sm font-medium text-gray-700 mb-2">
                        Prénoms
                      </label>
                      <input
                        type="text"
                        id="prenoms"
                        name="prenoms"
                        value={formData.prenoms}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Adresse Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={isEditing}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <input
                        type="tel"
                        id="telephone"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label htmlFor="adresse" className="block text-sm font-medium text-gray-700 mb-2">
                      Adresse
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        id="adresse"
                        name="adresse"
                        value={formData.adresse}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Informations personnelles complémentaires */}
                <div className="border-b border-gray-200 pb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Informations personnelles
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="date_naissance" className="block text-sm font-medium text-gray-700 mb-2">
                        Date de naissance
                      </label>
                      <input
                        type="date"
                        id="date_naissance"
                        name="date_naissance"
                        value={formData.date_naissance}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="lieu_naissance" className="block text-sm font-medium text-gray-700 mb-2">
                        Lieu de naissance
                      </label>
                      <input
                        type="text"
                        id="lieu_naissance"
                        name="lieu_naissance"
                        value={formData.lieu_naissance}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="nationalite" className="block text-sm font-medium text-gray-700 mb-2">
                        Nationalité
                      </label>
                      <input
                        type="text"
                        id="nationalite"
                        name="nationalite"
                        value={formData.nationalite}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="numero_piece_identite" className="block text-sm font-medium text-gray-700 mb-2">
                        N° Pièce d'identité
                      </label>
                      <input
                        type="text"
                        id="numero_piece_identite"
                        name="numero_piece_identite"
                        value={formData.numero_piece_identite}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Informations professionnelles */}
                <div className="border-b border-gray-200 pb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <Briefcase className="w-5 h-5 mr-2" />
                    Informations professionnelles
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="entreprise" className="block text-sm font-medium text-gray-700 mb-2">
                        Entreprise
                      </label>
                      <input
                        type="text"
                        id="entreprise"
                        name="entreprise"
                        value={formData.entreprise}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="profession" className="block text-sm font-medium text-gray-700 mb-2">
                        Profession
                      </label>
                      <input
                        type="text"
                        id="profession"
                        name="profession"
                        value={formData.profession}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="revenu_mensuel" className="block text-sm font-medium text-gray-700 mb-2">
                        Revenu mensuel (FCFA)
                      </label>
                      <input
                        type="number"
                        id="revenu_mensuel"
                        name="revenu_mensuel"
                        value={formData.revenu_mensuel}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="domiciliation_bancaire" className="block text-sm font-medium text-gray-700 mb-2">
                        Domiciliation bancaire
                      </label>
                      <input
                        type="text"
                        id="domiciliation_bancaire"
                        name="domiciliation_bancaire"
                        value={formData.domiciliation_bancaire}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Situation familiale */}
                <div className="pb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    Situation familiale
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label htmlFor="statut_marital" className="block text-sm font-medium text-gray-700 mb-2">
                        Statut marital
                      </label>
                      <select
                        id="statut_marital"
                        name="statut_marital"
                        value={formData.statut_marital}
                        onChange={handleInputChange}
                        disabled={!isEditing || refLoading}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      >
                        <option value="">Sélectionnez un statut</option>
                        {statutsMaritaux.map((statut) => (
                          <option key={statut.id} value={statut.id}>
                            {statut.libelle}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="regime_matrimonial" className="block text-sm font-medium text-gray-700 mb-2">
                        Régime matrimonial
                      </label>
                      <select
                        id="regime_matrimonial"
                        name="regime_matrimonial"
                        value={formData.regime_matrimonial}
                        onChange={handleInputChange}
                        disabled={!isEditing || refLoading}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      >
                        <option value="">Sélectionnez un régime</option>
                        {regimesMatrimoniaux.map((regime) => (
                          <option key={regime.id} value={regime.id}>
                            {regime.libelle}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="nombre_enfants" className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre d'enfants
                      </label>
                      <input
                        type="number"
                        id="nombre_enfants"
                        name="nombre_enfants"
                        value={formData.nombre_enfants}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        min="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>
    
            {/* Security Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Sécurité
                </h3>
                {!isChangingPassword ? (
                  <button
                    onClick={() => setIsChangingPassword(true)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Changer le mot de passe
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={handlePasswordCancel}
                      className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Annuler
                    </button>
                    <button
                      onClick={handlePasswordSubmit}
                      disabled={saving}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sauvegarde...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Sauvegarder
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {isChangingPassword ? (
                <>
                  {/* Messages d'erreur et de succès pour le mot de passe */}
                  {passwordError && (
                    <div className="mb-4">
                      <ErrorMessage error={passwordError} onDismiss={() => setPasswordError(null)} />
                    </div>
                  )}
                  
                  {passwordSuccess && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                        <p className="text-green-800">{passwordSuccess}</p>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handlePasswordSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Mot de passe actuel
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <input
                        type={showPasswords.current ? "text" : "password"}
                        id="currentPassword"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        required
                        className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Entrez votre mot de passe actuel"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('current')}
                        className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Nouveau mot de passe
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <input
                        type={showPasswords.new ? "text" : "password"}
                        id="newPassword"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        required
                        minLength={8}
                        className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Entrez votre nouveau mot de passe (min. 8 caractères)"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('new')}
                        className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Confirmer le nouveau mot de passe
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <input
                        type={showPasswords.confirm ? "text" : "password"}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        required
                        minLength={8}
                        className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Confirmez votre nouveau mot de passe"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('confirm')}
                        className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="text-sm text-gray-600">
                    <p>Le mot de passe doit contenir au moins 8 caractères.</p>
                  </div>
                </form>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-1">Mot de passe</h4>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
